/**
 * CatalogScreen — Catálogo de Agents real (F1-FE-5).
 *
 * Porta a tela `catalog` do protótipo com dados reais de GET /agents.
 *
 * KpiCards (4):
 *   - Total de agents (394)
 *   - Por tier (opus/sonnet/haiku)
 *   - Categorias únicas
 *   - 11 Engineering Standards
 *
 * Busca por nome/descrição (search), filtro por categoria e permission_level.
 * Paginação (limit/offset). Lista de agents com model, permissão, categoria.
 *
 * Agentes especialistas (SPEC A2 FR-22):
 *   - filtro "Sem prompt de especialista" (`prompt_status`) e KPI com a contagem;
 *   - na ficha: selo, checklist das seções, histórico de versões do tenant com diff e o
 *     seletor de perfil de ferramentas (PUT `tool_profile`);
 *   - no formulário do system_prompt, o checklist em tempo real (validarPrompt.js);
 *   - aba Skills: filtro "Sem conteúdo" e "Importar arquivo" (.md/.zip, arquivo cru).
 *
 * Identidade: paleta C, estilos inline, pt-BR, a11y.
 * Standards: Clean, NFR (a11y), OWASP.
 */
import { MOSTRAR_ENGINEERING_STANDARDS } from '@/lib/engineeringStandards';
import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Search, Filter, Bot, Shield, Cpu, Layers, ChevronLeft, ChevronRight,
  BookOpen, X, Puzzle, Pencil, Plus, Trash2, Link2, Wrench, Upload, History,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, ForbiddenBanner, RateLimitBanner } from '@/components/ui';
import { LoadingSpinner } from '@/components/ui/QueryState';
import {
  useAgents, useUpdateAgent, useCreateAgent, useAssignSkill, useUnassignSkill,
} from '@/api/agents';
import {
  useSkills, useCreateSkill, useUpdateSkill, useDeleteSkill, useImportSkill, useImportSkillFile,
} from '@/api/skills';
import { useMcpServers } from '@/api/mcp';
import { validarPrompt, ehPapelGenerico } from '@/lib/validarPrompt';
import { ChecklistSecoes } from '@/screens/journey/steps/squad/ChecklistSecoes';
import { HistoricoVersoes } from '@/screens/journey/steps/squad/HistoricoVersoes';
import { PERFIS, TEXTO_DO_PERFIL } from '@/screens/journey/steps/squad/comum';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

/** Selo do prompt de especialista pelo `prompt_status` do AgentOut (A1 FR-21). */
const SELO_DO_PROMPT = {
  especialista: { texto: '✅ especialista', cor: C.cyber },
  incompleto: { texto: '⚠️ incompleto', cor: C.warn },
  sem_prompt: { texto: '⚠️ sem prompt de especialista', cor: C.warn },
};

/** Slug do agente para as rotas por nome (/agents/{nome}/prompt…). */
const slugDoAgente = (a) => a?.slug ?? a?.name ?? a?.id;

/** Parâmetro da URL (deep-link "Abrir no Catálogo"), sem depender do Router. */
function parametroDaUrl(nome) {
  try {
    return new URLSearchParams(window.location.search).get(nome) || '';
  } catch {
    return '';
  }
}

function SeloDoPrompt({ status }) {
  const s = SELO_DO_PROMPT[status];
  if (!s) return null;
  return (
    <span data-testid="selo-prompt-catalogo"
      style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap',
        color: s.cor, background: `${s.cor}18`, border: `1px solid ${s.cor}55` }}>
      {s.texto}
    </span>
  );
}

/** Categorias de exibição (do protótipo). */
const CATEGORIES = [
  'Orquestração',
  'Arquitetura & Core',
  'Linguagens',
  'Legadas & Enterprise',
  'Frontend',
  'Bancos de Dados',
  'Infra & Cloud',
  'Data & IA',
  'Qualidade',
  'Segurança',
];

/** Mapeamento de permission_level → cor + label. */
const PERM = {
  'Read-only':    { color: C.hot,        label: 'Read-only'    },
  'Developer':    { color: C.neutralMuted, label: 'Developer'  },
  'Orchestrator': { color: C.digital,    label: 'Orchestrator' },
};

/** Mapeamento de model → cor. */
const MODEL_COLOR = {
  opus:   C.quantum,
  sonnet: C.cyber,
  haiku:  C.neon,
};

/** Modelos válidos (CHECK da tabela agent_catalog). */
const MODEL_OPTIONS = ['haiku', 'sonnet', 'opus'];

/** Níveis de permissão válidos (CHECK da tabela agent_catalog). */
const PERMISSION_OPTIONS = [
  'orchestrator',
  'read_only',
  'advisory',
  'developer',
  'developer_web',
];

/** Estilo base compartilhado pelos campos do formulário de edição. */
const FIELD_STYLE = {
  width: '100%',
  padding: '8px 12px',
  backgroundColor: C.blackElev2,
  border: `1px solid ${C.blackBorder}`,
  borderRadius: 6,
  fontSize: 13,
  color: C.neutral,
  outline: 'none',
  boxSizing: 'border-box',
};

const LABEL_STYLE = {
  display: 'block',
  fontSize: 11,
  color: C.neutralMuted,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  marginBottom: 6,
};

/** Origem das skills → cor + label legível. */
const SKILL_SOURCE = {
  'anthropic':       { color: C.quantum, label: 'Anthropic'       },
  'tech-leads-club': { color: C.cyber,   label: 'Tech Leads Club' },
  'marketing':       { color: C.neon,    label: 'Marketing'       },
};

const SKILL_PAGE_SIZE = 20;

const TH_STYLE = {
  textAlign: 'left',
  padding: '10px 16px',
  fontSize: 11,
  fontWeight: 600,
  color: C.neutralMuted,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: `1px solid ${C.blackBorder}`,
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  padding: '12px 16px',
  fontSize: 13,
  verticalAlign: 'middle',
  borderBottom: `1px solid ${C.blackBorder}`,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Mensagem amigável (pt-BR) a partir de um erro de mutação. */
function friendlyError(error, fallback = 'Não foi possível concluir a ação. Tente novamente.') {
  if (!error) return null;
  if (error.status === 403) return 'Sem permissão para esta ação.';
  return fallback;
}

/** Bloco de alerta reutilizável (paleta C). */
function AlertBox({ children }) {
  return (
    <div
      role="alert"
      style={{
        fontSize: 12,
        color: C.hot,
        marginBottom: 12,
        padding: '8px 12px',
        backgroundColor: `${C.hot}18`,
        border: `1px solid ${C.hot}44`,
        borderRadius: 6,
      }}
    >
      {children}
    </div>
  );
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

/**
 * FormModal — casca reutilizável de modal com formulário (paleta C, a11y).
 * Reutiliza o mesmo visual dos modais de detalhe existentes.
 */
function FormModal({ title, icon: Icon, iconColor = C.cyber, ariaLabel, onClose, children }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 210,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(12,12,12,0.85)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: C.blackElev,
          border: `1px solid ${C.blackBorder}`,
          borderRadius: 12,
          padding: 28,
          width: '100%',
          maxWidth: 560,
          maxHeight: '85vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Fechar"
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', cursor: 'pointer', color: C.neutralMuted, padding: 4, borderRadius: 4,
          }}
        >
          <X size={16} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          {Icon && (
            <div
              style={{
                width: 40, height: 40, borderRadius: 10,
                backgroundColor: `${iconColor}22`, border: `1px solid ${iconColor}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              aria-hidden="true"
            >
              <Icon size={20} style={{ color: iconColor }} />
            </div>
          )}
          <div style={{ fontSize: 18, fontWeight: 700, color: C.neutral }}>{title}</div>
        </div>

        {children}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, sub }) {
  return (
    <Card>
      <div className="flex items-start justify-between mb-3">
        <div style={{ fontSize: 12, color: C.neutralDim, letterSpacing: '0.04em' }}>
          {label}
        </div>
        <Icon size={16} style={{ color }} aria-hidden="true" />
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: C.neutral,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        {value ?? '—'}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: C.neutralDim, marginTop: 6 }}>{sub}</div>
      )}
    </Card>
  );
}

function AgentRow({ agent, onSelect }) {
  const modelColor = MODEL_COLOR[agent.model?.toLowerCase()] ?? C.neutralMuted;
  const perm       = PERM[agent.permission_level] ?? { color: C.neutralMuted, label: agent.permission_level ?? '—' };

  return (
    <tr
      tabIndex={0}
      role="row"
      onClick={() => onSelect(agent)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(agent); }}
      style={{ cursor: 'pointer' }}
      className="hover:opacity-80"
    >
      {/* Nome */}
      <td style={TD_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              backgroundColor: `${modelColor}22`,
              border: `1px solid ${modelColor}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <Bot size={14} style={{ color: modelColor }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: C.neutral, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              {agent.name ?? agent.id}
              {agent.in_use && (
                <span
                  title={`Cabeado no pipeline: ${(agent.used_in_steps || []).join(', ')}`}
                  style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                    textTransform: 'uppercase', color: C.cyber,
                    border: `1px solid ${C.cyber}`, borderRadius: 999, padding: '1px 6px',
                  }}
                >
                  em uso
                </span>
              )}
            </div>
            {agent.description && (
              <div
                style={{
                  fontSize: 11,
                  color: C.neutralMuted,
                  maxWidth: 280,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {agent.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Modelo */}
      <td style={TD_STYLE}>
        <Badge color={modelColor} light>
          {agent.model ?? '—'}
        </Badge>
      </td>

      {/* Categoria */}
      <td style={TD_STYLE}>
        <span style={{ fontSize: 12, color: C.neutralDim }}>
          {agent.category ?? '—'}
        </span>
      </td>

      {/* Permissão */}
      <td style={TD_STYLE}>
        <Badge color={perm.color} light>
          {perm.label}
        </Badge>
      </td>

      {/* Skills */}
      <td style={{ ...TD_STYLE, color: C.neutralMuted, fontSize: 12 }}>
        {Array.isArray(agent.skills) ? agent.skills.length : '—'}
      </td>

      {/* Prompt de especialista (A2 FR-22) */}
      <td style={TD_STYLE}>
        <SeloDoPrompt status={agent.prompt_status} />
        {agent.status === 'rascunho' && (
          <span style={{ marginLeft: 6, fontSize: 10.5, color: C.warn, fontWeight: 600 }}>rascunho</span>
        )}
      </td>
    </tr>
  );
}

function AgentDetailModal({ agent, onClose }) {
  const updateAgent   = useUpdateAgent();
  const assignSkill   = useAssignSkill();
  const unassignSkill = useUnassignSkill();
  const [editing, setEditing]       = useState(false);
  const [localAgent, setLocalAgent] = useState(agent);
  const [form, setForm]             = useState(() => _initForm(agent));
  const [skillToAdd, setSkillToAdd] = useState('');
  const updatePerfil = useUpdateAgent();
  const [verHistorico, setVerHistorico] = useState(false);

  // Skills disponíveis para atribuir (catálogo completo).
  const { data: skillsData } = useSkills({ limit: 200 });
  const allSkillNames = useMemo(() => {
    const arr = Array.isArray(skillsData) ? skillsData : (skillsData?.items ?? []);
    return arr.map((s) => s.name).filter(Boolean);
  }, [skillsData]);

  // Reinicializa quando o agente selecionado muda.
  useEffect(() => {
    setLocalAgent(agent);
    setForm(_initForm(agent));
    setEditing(false);
    setSkillToAdd('');
    updateAgent.reset();
    assignSkill.reset();
    unassignSkill.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  if (!localAgent) return null;

  const nomeDoAgente = slugDoAgente(localAgent);
  const papelGenerico = ehPapelGenerico(nomeDoAgente);
  const validacaoAtual = validarPrompt(localAgent.system_prompt ?? '', { papelGenerico });
  const validacaoForm = validarPrompt(form.system_prompt ?? '', { papelGenerico });
  const perfilAtual = PERFIS.includes(localAgent.tool_profile) ? localAgent.tool_profile : '';

  const handlePerfil = (e) => {
    const tool_profile = e.target.value;
    if (!tool_profile) return;
    updatePerfil.mutate(
      { id: localAgent.id, body: { tool_profile } },
      { onSuccess: (data) => setLocalAgent((prev) => ({ ...prev, ...(data || {}), tool_profile })) },
    );
  };

  const modelColor = MODEL_COLOR[localAgent.model?.toLowerCase()] ?? C.neutralMuted;
  const perm       = PERM[localAgent.permission_level]
    ?? { color: C.neutralMuted, label: localAgent.permission_level ?? '—' };

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    setForm(_initForm(localAgent));
    setEditing(false);
    updateAgent.reset();
  };

  const handleSave = () => {
    updateAgent.mutate(
      {
        id: localAgent.id,
        body: {
          description:      form.description,
          model:            form.model,
          permission_level: form.permission_level,
          category:         form.category,
          system_prompt:    form.system_prompt,
          is_active:        form.is_active,
        },
      },
      {
        onSuccess: (data) => {
          // Reflete os novos dados no modal (merge do retorno + campos editados).
          setLocalAgent((prev) => ({
            ...prev,
            ...(data || {}),
            model:            form.model,
            permission_level: form.permission_level,
            description:      form.description,
            category:         form.category,
            system_prompt:    form.system_prompt,
            is_active:        form.is_active,
          }));
          setEditing(false);
        },
      },
    );
  };

  const isForbidden = updateAgent.error?.status === 403;
  const errorMsg = updateAgent.isError
    ? (isForbidden
        ? 'Sem permissão para editar este agente.'
        : 'Não foi possível salvar as alterações. Tente novamente.')
    : null;

  const currentSkills = Array.isArray(localAgent.skills) ? localAgent.skills : [];
  const skillOptions  = allSkillNames.filter((n) => !currentSkills.includes(n));
  const skillBusy     = assignSkill.isPending || unassignSkill.isPending;
  const skillErrorMsg = friendlyError(
    assignSkill.error || unassignSkill.error,
    'Não foi possível atualizar as skills. Tente novamente.',
  );

  const handleAddSkill = () => {
    if (!skillToAdd) return;
    assignSkill.mutate(
      { agentName: localAgent.id, skillName: skillToAdd },
      {
        onSuccess: () => {
          setLocalAgent((prev) => ({
            ...prev,
            skills: [...(Array.isArray(prev.skills) ? prev.skills : []).filter((s) => s !== skillToAdd), skillToAdd],
          }));
          setSkillToAdd('');
        },
      },
    );
  };

  const handleRemoveSkill = (sk) => {
    unassignSkill.mutate(
      { agentName: localAgent.id, skillName: sk },
      {
        onSuccess: () => {
          setLocalAgent((prev) => ({
            ...prev,
            skills: (Array.isArray(prev.skills) ? prev.skills : []).filter((s) => s !== sk),
          }));
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Detalhe do agente ${localAgent.name ?? localAgent.id}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(12,12,12,0.85)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: C.blackElev,
          border: `1px solid ${C.blackBorder}`,
          borderRadius: 12,
          padding: 28,
          width: '100%',
          maxWidth: 560,
          maxHeight: '80vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fechar */}
        <button
          onClick={onClose}
          aria-label="Fechar detalhes do agente"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: C.neutralMuted,
            padding: 4,
            borderRadius: 4,
          }}
        >
          <X size={16} />
        </button>

        {/* Cabeçalho */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              backgroundColor: `${modelColor}22`,
              border: `1px solid ${modelColor}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden="true"
          >
            <Bot size={22} style={{ color: modelColor }} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.neutral, marginBottom: 4 }}>
              {localAgent.name ?? localAgent.id}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Badge color={modelColor} light>{localAgent.model ?? '—'}</Badge>
              <Badge color={perm.color} light>{perm.label}</Badge>
              {localAgent.is_active === false && (
                <Badge color={C.neutralMuted} light>Inativo</Badge>
              )}
              <SeloDoPrompt status={localAgent.prompt_status} />
              {localAgent.status === 'rascunho' && <Badge color={C.warn} light>Rascunho</Badge>}
            </div>
          </div>
        </div>

        {/* Modo edição */}
        {editing ? (
          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(); }}
            aria-label="Editar agente"
          >
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="agent-edit-description" style={LABEL_STYLE}>Descrição</label>
              <textarea
                id="agent-edit-description"
                value={form.description}
                onChange={setField('description')}
                rows={3}
                style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px' }}>
                <label htmlFor="agent-edit-model" style={LABEL_STYLE}>Modelo</label>
                <select
                  id="agent-edit-model"
                  value={form.model}
                  onChange={setField('model')}
                  style={{ ...FIELD_STYLE, cursor: 'pointer', appearance: 'none' }}
                >
                  {MODEL_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: '1 1 160px' }}>
                <label htmlFor="agent-edit-permission" style={LABEL_STYLE}>Permissão</label>
                <select
                  id="agent-edit-permission"
                  value={form.permission_level}
                  onChange={setField('permission_level')}
                  style={{ ...FIELD_STYLE, cursor: 'pointer', appearance: 'none' }}
                >
                  {PERMISSION_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label htmlFor="agent-edit-category" style={LABEL_STYLE}>Categoria</label>
              <input
                id="agent-edit-category"
                type="text"
                value={form.category}
                onChange={setField('category')}
                style={FIELD_STYLE}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label htmlFor="agent-edit-system-prompt" style={LABEL_STYLE}>System prompt</label>
              <textarea
                id="agent-edit-system-prompt"
                value={form.system_prompt}
                onChange={setField('system_prompt')}
                rows={8}
                placeholder="Instruções de sistema do agente..."
                style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5, fontFamily: 'monospace' }}
              />
              <div style={{ marginTop: 8 }}>
                <ChecklistSecoes validacao={validacaoForm} titulo="Seções do prompt (tempo real)" />
              </div>
            </div>

            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 18, fontSize: 13, color: C.neutralDim, cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={setField('is_active')}
                style={{ cursor: 'pointer' }}
              />
              Agente ativo
            </label>

            {errorMsg && (
              <div
                role="alert"
                style={{
                  fontSize: 12,
                  color: C.hot,
                  marginBottom: 12,
                  padding: '8px 12px',
                  backgroundColor: `${C.hot}18`,
                  border: `1px solid ${C.hot}44`,
                  borderRadius: 6,
                }}
              >
                {errorMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn
                type="submit"
                size="sm"
                disabled={updateAgent.isPending}
              >
                {updateAgent.isPending ? 'Salvando...' : 'Salvar'}
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={updateAgent.isPending}
              >
                Cancelar
              </Btn>
            </div>
          </form>
        ) : (
        <>
        {/* Descrição */}
        {localAgent.description && (
          <p style={{ fontSize: 13, color: C.neutralDim, marginBottom: 16, lineHeight: 1.6 }}>
            {localAgent.description}
          </p>
        )}

        {/* Categoria */}
        {localAgent.category && (
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.05em' }}>
              CATEGORIA
            </span>
            <div style={{ fontSize: 13, color: C.neutral, marginTop: 2 }}>
              {localAgent.category}
            </div>
          </div>
        )}

        {/* System prompt */}
        {localAgent.system_prompt && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.05em' }}>
              SYSTEM PROMPT
            </span>
            <pre
              style={{
                marginTop: 6,
                padding: '10px 12px',
                backgroundColor: C.blackElev2,
                border: `1px solid ${C.blackBorder}`,
                borderRadius: 6,
                fontSize: 12,
                color: C.neutralDim,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 200,
                overflowY: 'auto',
                fontFamily: 'monospace',
              }}
            >
              {localAgent.system_prompt}
            </pre>
          </div>
        )}

        {/* Checklist das seções do prompt atual (A2 FR-22b) */}
        <div style={{ marginBottom: 16 }}>
          <ChecklistSecoes validacao={validacaoAtual} titulo="Seções do prompt atual" />
        </div>

        {/* Perfil de ferramentas (A2 FR-22b, FR-16) */}
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="agent-tool-profile" style={LABEL_STYLE}>Perfil de ferramentas</label>
          <select
            id="agent-tool-profile"
            value={perfilAtual}
            onChange={handlePerfil}
            disabled={updatePerfil.isPending}
            style={{ ...FIELD_STYLE, cursor: 'pointer', appearance: 'none' }}
          >
            {!perfilAtual && <option value="">— derivado da permissão —</option>}
            {PERFIS.map((p) => (
              <option key={p} value={p}>{p} — {TEXTO_DO_PERFIL[p]}</option>
            ))}
          </select>
          {updatePerfil.isSuccess && (
            <div role="status" style={{ fontSize: 11.5, color: C.cyber, marginTop: 4 }}>Perfil de ferramentas salvo.</div>
          )}
          {updatePerfil.isError && (
            <div style={{ marginTop: 6 }}>
              <AlertBox>{friendlyError(updatePerfil.error, 'Não foi possível salvar o perfil de ferramentas.')}</AlertBox>
            </div>
          )}
        </div>

        {/* Histórico de versões do tenant, com diff (A2 FR-22b) */}
        <div style={{ marginBottom: 16 }}>
          <Btn variant="ghost" size="sm" icon={History} onClick={() => setVerHistorico((v) => !v)}>
            {verHistorico ? 'Ocultar histórico de versões' : 'Histórico de versões do tenant'}
          </Btn>
          {verHistorico && (
            <div style={{ marginTop: 8 }}>
              <HistoricoVersoes nome={nomeDoAgente} escopos={['tenant']} />
            </div>
          )}
        </div>

        {/* Botão Editar */}
        <div style={{ marginBottom: 16 }}>
          <Btn variant="ghost" size="sm" icon={Pencil} onClick={() => setEditing(true)}>
            Editar
          </Btn>
        </div>

        {/* Skills — gerenciáveis (atribuir / remover) */}
        <div style={{ marginBottom: 16 }}>
          <span
            style={{
              fontSize: 11,
              color: C.neutralMuted,
              letterSpacing: '0.05em',
              display: 'block',
              marginBottom: 6,
            }}
          >
            SKILLS ({currentSkills.length})
          </span>

          {currentSkills.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {currentSkills.map((sk) => (
                <span
                  key={sk}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    color: C.cyber,
                    backgroundColor: `${C.cyber}22`,
                    border: `1px solid ${C.cyber}55`,
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '3px 6px 3px 8px',
                    borderRadius: 4,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                  }}
                >
                  {sk}
                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(sk)}
                    disabled={skillBusy}
                    aria-label={`Remover skill ${sk}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: skillBusy ? 'not-allowed' : 'pointer',
                      color: C.cyber,
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: C.neutralDim, marginBottom: 10 }}>
              Nenhuma skill atribuída.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={skillToAdd}
              onChange={(e) => setSkillToAdd(e.target.value)}
              aria-label="Selecionar skill para atribuir"
              disabled={skillBusy}
              style={{ ...FIELD_STYLE, width: 'auto', flex: '1 1 200px', cursor: 'pointer', appearance: 'none' }}
            >
              <option value="">Selecione uma skill...</option>
              {skillOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <Btn
              variant="outline"
              size="sm"
              icon={Plus}
              onClick={handleAddSkill}
              disabled={!skillToAdd || skillBusy}
            >
              {assignSkill.isPending ? 'Adicionando...' : 'Adicionar skill'}
            </Btn>
          </div>

          {skillErrorMsg && (
            <div style={{ marginTop: 10 }}>
              <AlertBox>{skillErrorMsg}</AlertBox>
            </div>
          )}
        </div>

        {/* Standards */}
        {MOSTRAR_ENGINEERING_STANDARDS && Array.isArray(localAgent.standards) && localAgent.standards.length > 0 && (
          <div>
            <span
              style={{
                fontSize: 11,
                color: C.neutralMuted,
                letterSpacing: '0.05em',
                display: 'block',
                marginBottom: 6,
              }}
            >
              ENGINEERING STANDARDS ({localAgent.standards.length})
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {localAgent.standards.map((st) => (
                <Badge key={st} color={C.quantum} light style={{ fontSize: 11 }}>
                  {st}
                </Badge>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

/** Inicializa o estado do formulário de edição a partir de um agente. */
function _initForm(agent) {
  return {
    description:      agent?.description ?? '',
    model:            (agent?.model ?? agent?.default_model_hint ?? 'sonnet').toLowerCase(),
    permission_level: agent?.permission_level ?? 'developer',
    category:         agent?.category ?? '',
    system_prompt:    agent?.system_prompt ?? '',
    is_active:        agent?.is_active ?? true,
  };
}

// ─── Criar agente ─────────────────────────────────────────────────────────────

function CreateAgentModal({ onClose }) {
  const createAgent = useCreateAgent();
  const [form, setForm] = useState({
    name: '', description: '', model: 'sonnet',
    permission_level: 'developer', category: '', system_prompt: '',
  });

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    createAgent.mutate(
      {
        name:             form.name,
        description:      form.description,
        model:            form.model,
        permission_level: form.permission_level,
        category:         form.category,
        system_prompt:    form.system_prompt || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  const errorMsg = friendlyError(createAgent.error, 'Não foi possível criar o agente. Tente novamente.');

  return (
    <FormModal title="Novo agente" icon={Bot} ariaLabel="Criar novo agente" onClose={onClose}>
      <form onSubmit={handleSubmit} aria-label="Criar agente">
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="agent-new-name" style={LABEL_STYLE}>Nome</label>
          <input
            id="agent-new-name"
            type="text"
            required
            value={form.name}
            onChange={setField('name')}
            style={FIELD_STYLE}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="agent-new-description" style={LABEL_STYLE}>Descrição</label>
          <textarea
            id="agent-new-description"
            value={form.description}
            onChange={setField('description')}
            rows={3}
            style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5 }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label htmlFor="agent-new-model" style={LABEL_STYLE}>Modelo</label>
            <select
              id="agent-new-model"
              value={form.model}
              onChange={setField('model')}
              style={{ ...FIELD_STYLE, cursor: 'pointer', appearance: 'none' }}
            >
              {MODEL_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 160px' }}>
            <label htmlFor="agent-new-permission" style={LABEL_STYLE}>Permissão</label>
            <select
              id="agent-new-permission"
              value={form.permission_level}
              onChange={setField('permission_level')}
              style={{ ...FIELD_STYLE, cursor: 'pointer', appearance: 'none' }}
            >
              {PERMISSION_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="agent-new-category" style={LABEL_STYLE}>Categoria</label>
          <input
            id="agent-new-category"
            type="text"
            value={form.category}
            onChange={setField('category')}
            style={FIELD_STYLE}
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label htmlFor="agent-new-system-prompt" style={LABEL_STYLE}>System prompt</label>
          <textarea
            id="agent-new-system-prompt"
            value={form.system_prompt}
            onChange={setField('system_prompt')}
            rows={6}
            placeholder="Instruções de sistema do agente..."
            style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5, fontFamily: 'monospace' }}
          />
        </div>

        {errorMsg && <AlertBox>{errorMsg}</AlertBox>}

        <div style={{ display: 'flex', gap: 10 }}>
          <Btn type="submit" size="sm" disabled={createAgent.isPending || !form.name.trim()}>
            {createAgent.isPending ? 'Criando...' : 'Criar agente'}
          </Btn>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={createAgent.isPending}>
            Cancelar
          </Btn>
        </div>
      </form>
    </FormModal>
  );
}

// ─── Aba (Agents / Skills) ─────────────────────────────────────────────────────

function TabButton({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        background: 'none',
        border: 'none',
        borderBottom: `2px solid ${active ? C.cyber : 'transparent'}`,
        color: active ? C.neutral : C.neutralMuted,
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      <Icon size={15} aria-hidden="true" />
      {label}
      <span
        style={{
          fontSize: 11,
          padding: '1px 7px',
          borderRadius: 999,
          backgroundColor: active ? `${C.cyber}22` : C.blackElev2,
          color: active ? C.cyber : C.neutralMuted,
        }}
      >
        {count}
      </span>
    </button>
  );
}

// ─── Skills ─────────────────────────────────────────────────────────────────

function SkillRow({ skill, onSelect }) {
  const src = SKILL_SOURCE[skill.source] ?? { color: C.neutralMuted, label: skill.source ?? '—' };

  return (
    <tr
      tabIndex={0}
      role="row"
      onClick={() => onSelect(skill)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(skill); }}
      style={{ cursor: 'pointer' }}
      className="hover:opacity-80"
    >
      {/* Nome + descrição */}
      <td style={TD_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              backgroundColor: `${src.color}22`,
              border: `1px solid ${src.color}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <Puzzle size={14} style={{ color: src.color }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: C.neutral, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              {skill.name}
              {skill.tem_conteudo === false && (
                <span title="Skill sem conteúdo — só nome e descrição"
                  style={{ fontSize: 10, fontWeight: 600, color: C.warn, border: `1px solid ${C.warn}`,
                    borderRadius: 999, padding: '1px 6px' }}>
                  ⚠️ sem conteúdo
                </span>
              )}
            </div>
            {skill.description && (
              <div
                style={{
                  fontSize: 11,
                  color: C.neutralMuted,
                  maxWidth: 420,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {skill.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Origem */}
      <td style={TD_STYLE}>
        <Badge color={src.color} light>{src.label}</Badge>
      </td>

      {/* Categoria */}
      <td style={TD_STYLE}>
        <span style={{ fontSize: 12, color: C.neutralDim }}>
          {skill.category ?? '—'}
        </span>
      </td>
    </tr>
  );
}

// ─── Tool (skill executável) ──────────────────────────────────────────────────

/** Tools built-in disponíveis (executor "builtin") + parâmetros sugeridos. */
const BUILTIN_TOOLS = [
  {
    name: 'knowledge_search',
    description: 'Busca semântica na base de conhecimento do tenant.',
    parameters: {
      type: 'object',
      properties: { query: { type: 'string' }, k: { type: 'integer' } },
      required: ['query'],
    },
  },
  {
    name: 'http_get',
    description: 'Executa uma requisição HTTP GET em uma URL.',
    parameters: {
      type: 'object',
      properties: { url: { type: 'string' } },
      required: ['url'],
    },
  },
];

/** Inicializa o estado do formulário de tool a partir de uma skill. */
function _initToolForm(skill) {
  const md   = skill?.metadata ?? {};
  const tool = md.tool ?? {};
  return {
    isTool:      Boolean(md.is_tool),
    name:        tool.name ?? '',
    description: tool.description ?? '',
    parameters:  tool.parameters != null ? JSON.stringify(tool.parameters, null, 2) : '',
    executor:    tool.executor ?? 'builtin',
    mcpServerId: tool.mcp_server_id ?? '',
  };
}

/**
 * Compõe o `metadata` a partir do estado do formulário de tool.
 * Valida o JSON Schema de parâmetros; lança Error com mensagem pt-BR se inválido.
 *
 * @returns {object} metadata — `{}` quando não é tool; caso contrário
 *   `{ is_tool: true, tool: { name, description, parameters, executor, mcp_server_id } }`.
 */
function buildToolMetadata(toolForm) {
  if (!toolForm.isTool) return {};

  let parameters = {};
  const raw = (toolForm.parameters ?? '').trim();
  if (raw) {
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      throw new Error('Parâmetros (JSON Schema) inválidos: verifique o JSON.');
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Parâmetros (JSON Schema) devem ser um objeto JSON.');
    }
    parameters = parsed;
  }

  const executor = toolForm.executor === 'mcp' ? 'mcp' : 'builtin';
  return {
    is_tool: true,
    tool: {
      name:          toolForm.name.trim(),
      description:   toolForm.description,
      parameters,
      executor,
      mcp_server_id: executor === 'mcp' ? (toolForm.mcpServerId || null) : null,
    },
  };
}

/**
 * ToolFieldset — seção "Ferramenta (tool)" reutilizada nos formulários de
 * criar/editar skill. Marca a skill como tool executável e compõe o metadata.
 *
 * @param {{ value: object, onChange: (next: object) => void, idPrefix: string }} props
 */
function ToolFieldset({ value, onChange, idPrefix }) {
  const { data: serversData } = useMcpServers();
  const servers = Array.isArray(serversData) ? serversData : [];

  const set = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange({ ...value, [key]: v });
  };

  const applyBuiltin = () => {
    const match = BUILTIN_TOOLS.find((t) => t.name === value.name.trim());
    if (!match) return;
    onChange({
      ...value,
      description: value.description || match.description,
      parameters: JSON.stringify(match.parameters, null, 2),
    });
  };

  return (
    <fieldset
      style={{
        border: `1px solid ${C.blackBorder}`,
        borderRadius: 8,
        padding: 14,
        marginBottom: 18,
        minInlineSize: 'auto',
      }}
    >
      <legend
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: 600, color: C.neutral, padding: '0 6px',
        }}
      >
        <Wrench size={13} style={{ color: C.quantum }} aria-hidden="true" />
        Ferramenta (tool)
      </legend>

      <label
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 13, color: C.neutralDim, cursor: 'pointer',
          marginBottom: value.isTool ? 14 : 0,
        }}
      >
        <input
          type="checkbox"
          checked={value.isTool}
          onChange={set('isTool')}
          style={{ cursor: 'pointer' }}
        />
        Esta skill é uma tool executável
      </label>

      {value.isTool && (
        <>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor={`${idPrefix}-tool-name`} style={LABEL_STYLE}>Nome da tool</label>
            <input
              id={`${idPrefix}-tool-name`}
              type="text"
              list={`${idPrefix}-tool-builtins`}
              value={value.name}
              onChange={set('name')}
              onBlur={applyBuiltin}
              placeholder="knowledge_search, http_get..."
              style={FIELD_STYLE}
            />
            <datalist id={`${idPrefix}-tool-builtins`}>
              {BUILTIN_TOOLS.map((t) => (
                <option key={t.name} value={t.name}>{t.description}</option>
              ))}
            </datalist>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor={`${idPrefix}-tool-description`} style={LABEL_STYLE}>Descrição da tool</label>
            <textarea
              id={`${idPrefix}-tool-description`}
              value={value.description}
              onChange={set('description')}
              rows={2}
              style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor={`${idPrefix}-tool-parameters`} style={LABEL_STYLE}>
              Parâmetros (JSON Schema)
            </label>
            <textarea
              id={`${idPrefix}-tool-parameters`}
              value={value.parameters}
              onChange={set('parameters')}
              rows={6}
              placeholder={'{\n  "type": "object",\n  "properties": { ... }\n}'}
              spellCheck={false}
              style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5, fontFamily: 'monospace' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px' }}>
              <label htmlFor={`${idPrefix}-tool-executor`} style={LABEL_STYLE}>Executor</label>
              <select
                id={`${idPrefix}-tool-executor`}
                value={value.executor}
                onChange={set('executor')}
                style={{ ...FIELD_STYLE, cursor: 'pointer', appearance: 'none' }}
              >
                <option value="builtin">builtin</option>
                <option value="mcp">mcp</option>
              </select>
            </div>

            {value.executor === 'mcp' && (
              <div style={{ flex: '1 1 200px' }}>
                <label htmlFor={`${idPrefix}-tool-mcp`} style={LABEL_STYLE}>Servidor MCP</label>
                <select
                  id={`${idPrefix}-tool-mcp`}
                  value={value.mcpServerId}
                  onChange={set('mcpServerId')}
                  style={{ ...FIELD_STYLE, cursor: 'pointer', appearance: 'none' }}
                >
                  <option value="">Selecione um servidor...</option>
                  {servers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {servers.length === 0 && (
                  <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 6 }}>
                    Nenhum servidor MCP cadastrado. Adicione um em Configurações.
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </fieldset>
  );
}

function SkillDetailModal({ skill: skillProp, onClose }) {
  const updateSkill = useUpdateSkill();
  const deleteSkill = useDeleteSkill();
  const [editing, setEditing]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [local, setLocal]             = useState(skillProp);
  const [form, setForm]               = useState(() => ({
    description: skillProp?.description ?? '',
    content:     skillProp?.content ?? '',
    category:    skillProp?.category ?? '',
  }));
  const [toolForm, setToolForm]       = useState(() => _initToolForm(skillProp));
  const [toolError, setToolError]     = useState(null);

  useEffect(() => {
    setLocal(skillProp);
    setForm({
      description: skillProp?.description ?? '',
      content:     skillProp?.content ?? '',
      category:    skillProp?.category ?? '',
    });
    setToolForm(_initToolForm(skillProp));
    setToolError(null);
    setEditing(false);
    setConfirmDelete(false);
    updateSkill.reset();
    deleteSkill.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillProp]);

  if (!local) return null;
  const src = SKILL_SOURCE[local.source] ?? { color: C.neutralMuted, label: local.source ?? '—' };
  const isTool = Boolean(local.metadata?.is_tool);

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSaveEdit = (e) => {
    e.preventDefault();

    let metadata;
    try {
      metadata = buildToolMetadata(toolForm);
      setToolError(null);
    } catch (err) {
      setToolError(err.message);
      return;
    }

    updateSkill.mutate(
      {
        name: local.name,
        body: {
          description: form.description,
          content:     form.content,
          category:    form.category,
          metadata,
        },
      },
      {
        onSuccess: (data) => {
          setLocal((prev) => ({ ...prev, ...(data || {}), ...form, metadata }));
          setEditing(false);
        },
      },
    );
  };

  const handleDelete = () => {
    deleteSkill.mutate(
      { name: local.name },
      { onSuccess: () => onClose() },
    );
  };

  const editErrorMsg   = friendlyError(updateSkill.error, 'Não foi possível salvar a skill. Tente novamente.');
  const deleteErrorMsg = friendlyError(deleteSkill.error, 'Não foi possível excluir a skill. Tente novamente.');
  const skill = local;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Detalhe da skill ${skill.name}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(12,12,12,0.85)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: C.blackElev,
          border: `1px solid ${C.blackBorder}`,
          borderRadius: 12,
          padding: 28,
          width: '100%',
          maxWidth: 560,
          maxHeight: '80vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Fechar detalhes da skill"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: C.neutralMuted,
            padding: 4,
            borderRadius: 4,
          }}
        >
          <X size={16} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              backgroundColor: `${src.color}22`,
              border: `1px solid ${src.color}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden="true"
          >
            <Puzzle size={22} style={{ color: src.color }} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.neutral, marginBottom: 4 }}>
              {skill.name}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Badge color={src.color} light>{src.label}</Badge>
              {skill.category && <Badge color={C.cyberDim} light>{skill.category}</Badge>}
              {isTool && (
                <Badge color={C.quantum} light>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Wrench size={11} aria-hidden="true" /> TOOL
                  </span>
                </Badge>
              )}
            </div>
          </div>
        </div>

        {editing ? (
          <form onSubmit={handleSaveEdit} aria-label="Editar skill">
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="skill-edit-description" style={LABEL_STYLE}>Descrição</label>
              <textarea
                id="skill-edit-description"
                value={form.description}
                onChange={setField('description')}
                rows={3}
                style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label htmlFor="skill-edit-category" style={LABEL_STYLE}>Categoria</label>
              <input
                id="skill-edit-category"
                type="text"
                value={form.category}
                onChange={setField('category')}
                style={FIELD_STYLE}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label htmlFor="skill-edit-content" style={LABEL_STYLE}>Conteúdo</label>
              <textarea
                id="skill-edit-content"
                value={form.content}
                onChange={setField('content')}
                rows={10}
                style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5, fontFamily: 'monospace' }}
              />
            </div>

            <ToolFieldset value={toolForm} onChange={setToolForm} idPrefix="skill-edit" />

            {toolError && <AlertBox>{toolError}</AlertBox>}
            {editErrorMsg && <AlertBox>{editErrorMsg}</AlertBox>}

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn type="submit" size="sm" disabled={updateSkill.isPending}>
                {updateSkill.isPending ? 'Salvando...' : 'Salvar'}
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => { setEditing(false); updateSkill.reset(); }}
                disabled={updateSkill.isPending}
              >
                Cancelar
              </Btn>
            </div>
          </form>
        ) : (
          <>
            {skill.description && (
              <p style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.6, marginBottom: 16 }}>
                {skill.description}
              </p>
            )}

            {/* Conteúdo da skill */}
            {skill.content && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.05em' }}>
                  CONTEÚDO
                </span>
                <pre
                  style={{
                    marginTop: 6,
                    padding: '10px 12px',
                    backgroundColor: C.blackElev2,
                    border: `1px solid ${C.blackBorder}`,
                    borderRadius: 6,
                    fontSize: 12,
                    color: C.neutralDim,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 260,
                    overflowY: 'auto',
                    fontFamily: 'monospace',
                  }}
                >
                  {skill.content}
                </pre>
              </div>
            )}

            {deleteErrorMsg && <AlertBox>{deleteErrorMsg}</AlertBox>}

            {/* Ações: Editar / Excluir */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Btn variant="ghost" size="sm" icon={Pencil} onClick={() => setEditing(true)}>
                Editar
              </Btn>
              {confirmDelete ? (
                <>
                  <Btn
                    variant="danger"
                    size="sm"
                    icon={Trash2}
                    onClick={handleDelete}
                    disabled={deleteSkill.isPending}
                  >
                    {deleteSkill.isPending ? 'Excluindo...' : 'Confirmar exclusão'}
                  </Btn>
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleteSkill.isPending}
                  >
                    Cancelar
                  </Btn>
                </>
              ) : (
                <Btn variant="danger" size="sm" icon={Trash2} onClick={() => setConfirmDelete(true)}>
                  Excluir
                </Btn>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Criar / importar skill ───────────────────────────────────────────────────

function CreateSkillModal({ onClose }) {
  const createSkill = useCreateSkill();
  const [form, setForm] = useState({ name: '', description: '', content: '', category: '' });
  const [toolForm, setToolForm] = useState(() => _initToolForm(null));
  const [toolError, setToolError] = useState(null);
  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();

    let metadata;
    try {
      metadata = buildToolMetadata(toolForm);
      setToolError(null);
    } catch (err) {
      setToolError(err.message);
      return;
    }

    createSkill.mutate(
      {
        name:        form.name,
        description: form.description,
        content:     form.content || undefined,
        category:    form.category || undefined,
        // Só envia metadata quando a skill é marcada como tool.
        ...(toolForm.isTool ? { metadata } : {}),
      },
      { onSuccess: () => onClose() },
    );
  };

  const errorMsg = friendlyError(createSkill.error, 'Não foi possível criar a skill. Tente novamente.');

  return (
    <FormModal title="Nova skill" icon={Puzzle} ariaLabel="Criar nova skill" onClose={onClose}>
      <form onSubmit={handleSubmit} aria-label="Criar skill">
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="skill-new-name" style={LABEL_STYLE}>Nome</label>
          <input
            id="skill-new-name"
            type="text"
            required
            value={form.name}
            onChange={setField('name')}
            style={FIELD_STYLE}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="skill-new-description" style={LABEL_STYLE}>Descrição</label>
          <textarea
            id="skill-new-description"
            value={form.description}
            onChange={setField('description')}
            rows={3}
            style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5 }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="skill-new-category" style={LABEL_STYLE}>Categoria</label>
          <input
            id="skill-new-category"
            type="text"
            value={form.category}
            onChange={setField('category')}
            style={FIELD_STYLE}
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label htmlFor="skill-new-content" style={LABEL_STYLE}>Conteúdo</label>
          <textarea
            id="skill-new-content"
            value={form.content}
            onChange={setField('content')}
            rows={10}
            placeholder="Conteúdo da skill (markdown)..."
            style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: 1.5, fontFamily: 'monospace' }}
          />
        </div>

        <ToolFieldset value={toolForm} onChange={setToolForm} idPrefix="skill-new" />

        {toolError && <AlertBox>{toolError}</AlertBox>}
        {errorMsg && <AlertBox>{errorMsg}</AlertBox>}

        <div style={{ display: 'flex', gap: 10 }}>
          <Btn type="submit" size="sm" disabled={createSkill.isPending || !form.name.trim()}>
            {createSkill.isPending ? 'Criando...' : 'Criar skill'}
          </Btn>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={createSkill.isPending}>
            Cancelar
          </Btn>
        </div>
      </form>
    </FormModal>
  );
}

function ImportSkillModal({ onClose }) {
  const importSkill = useImportSkill();
  const [form, setForm] = useState({ url: '', name: '' });
  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    importSkill.mutate(
      { url: form.url, name: form.name || undefined },
      { onSuccess: () => onClose() },
    );
  };

  const errorMsg = friendlyError(importSkill.error, 'Não foi possível importar a skill. Verifique a URL e tente novamente.');

  return (
    <FormModal title="Importar skill por URL" icon={Link2} ariaLabel="Importar skill por URL" onClose={onClose}>
      <form onSubmit={handleSubmit} aria-label="Importar skill">
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="skill-import-url" style={LABEL_STYLE}>URL</label>
          <input
            id="skill-import-url"
            type="url"
            required
            value={form.url}
            onChange={setField('url')}
            placeholder="https://..."
            style={FIELD_STYLE}
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label htmlFor="skill-import-name" style={LABEL_STYLE}>Nome (opcional)</label>
          <input
            id="skill-import-name"
            type="text"
            value={form.name}
            onChange={setField('name')}
            placeholder="Deixe em branco para usar o nome de origem"
            style={FIELD_STYLE}
          />
        </div>

        {errorMsg && <AlertBox>{errorMsg}</AlertBox>}

        <div style={{ display: 'flex', gap: 10 }}>
          <Btn type="submit" size="sm" disabled={importSkill.isPending || !form.url.trim()}>
            {importSkill.isPending ? 'Importando...' : 'Importar'}
          </Btn>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={importSkill.isPending}>
            Cancelar
          </Btn>
        </div>
      </form>
    </FormModal>
  );
}

/** Importa uma skill de arquivo .md ou .zip — o arquivo vai CRU no corpo (A1 FR-26). */
function ImportSkillFileModal({ onClose }) {
  const importFile = useImportSkillFile();
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    importFile.mutate({ file, name: name.trim() || undefined }, { onSuccess: () => onClose() });
  };

  const err = importFile.error;
  const segredos = Array.isArray(err?.problemBody?.segredos) ? err.problemBody.segredos : [];
  let errorMsg = null;
  if (err) {
    if (err.status === 413) errorMsg = 'Arquivo acima do limite de tamanho.';
    else if (err.status === 403) errorMsg = 'Sem permissão para esta ação.';
    else errorMsg = (typeof (err.problemBody?.detail ?? err.detail) === 'string' && (err.problemBody?.detail ?? err.detail))
      || 'Não foi possível importar o arquivo. Tente novamente.';
  }

  return (
    <FormModal title="Importar skill de arquivo" icon={Upload} ariaLabel="Importar skill de arquivo" onClose={onClose}>
      <form onSubmit={handleSubmit} aria-label="Importar skill de arquivo">
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="skill-import-file" style={LABEL_STYLE}>Arquivo (.md ou .zip)</label>
          <input
            id="skill-import-file"
            type="file"
            accept=".md,.zip,text/markdown,application/zip"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            style={{ ...FIELD_STYLE, padding: 6 }}
          />
          <div style={{ fontSize: 11.5, color: C.neutralMuted, marginTop: 6 }}>
            Um SKILL.md com frontmatter, ou um .zip com o pacote da skill (SKILL.md + arquivos).
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label htmlFor="skill-import-file-name" style={LABEL_STYLE}>Nome (opcional)</label>
          <input
            id="skill-import-file-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Deixe em branco para usar o nome do frontmatter"
            style={FIELD_STYLE}
          />
        </div>

        {errorMsg && (
          <AlertBox>
            {errorMsg}
            {segredos.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {segredos.map((sg, i) => <li key={`${sg.tipo}-${sg.linha}-${i}`}>{sg.tipo} — linha {sg.linha}</li>)}
              </ul>
            )}
          </AlertBox>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <Btn type="submit" size="sm" disabled={importFile.isPending || !file}>
            {importFile.isPending ? 'Importando...' : 'Importar'}
          </Btn>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={importFile.isPending}>
            Cancelar
          </Btn>
        </div>
      </form>
    </FormModal>
  );
}

function SkillsPanel() {
  const [search,        setSearch]        = useState(() => parametroDaUrl('search'));
  const [sourceFilter,  setSourceFilter]  = useState('');
  const [semConteudo,   setSemConteudo]   = useState(false);
  const [showImportFile, setShowImportFile] = useState(false);
  const [page,          setPage]          = useState(0);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [showCreateSkill, setShowCreateSkill] = useState(false);
  const [showImportSkill, setShowImportSkill] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState(() => parametroDaUrl('search'));
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, sourceFilter, semConteudo]);

  const filters = useMemo(() => ({
    search: debouncedSearch || undefined,
    source: sourceFilter    || undefined,
    sem_conteudo: semConteudo || undefined,
    limit:  SKILL_PAGE_SIZE,
    offset: page * SKILL_PAGE_SIZE,
  }), [debouncedSearch, sourceFilter, semConteudo, page]);

  const { data, isLoading, isFetching, error } = useSkills(filters);

  const skills = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.items ?? []);
  }, [data]);

  const total = useMemo(() => {
    if (!data) return 0;
    if (Array.isArray(data)) return data.length;
    return data.total ?? data.count ?? skills.length;
  }, [data, skills]);

  const totalPages = Math.ceil(total / SKILL_PAGE_SIZE);
  const hasFilters = Boolean(search || sourceFilter || semConteudo);
  const clearFilters = useCallback(() => { setSearch(''); setSourceFilter(''); setSemConteudo(false); }, []);

  if (error?.status === 403) return <ForbiddenBanner />;
  if (error?.status === 429) return <RateLimitBanner />;

  return (
    <div>
      {/* Ações do catálogo de skills */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Btn variant="outline" size="sm" icon={Plus} onClick={() => setShowCreateSkill(true)}>
          Nova skill
        </Btn>
        <Btn variant="ghost" size="sm" icon={Link2} onClick={() => setShowImportSkill(true)}>
          Importar por URL
        </Btn>
        <Btn variant="ghost" size="sm" icon={Upload} onClick={() => setShowImportFile(true)}>
          Importar arquivo
        </Btn>
      </div>

      {/* Busca e filtro de origem */}
      <Card style={{ marginBottom: 20, padding: '14px 20px' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <div style={{ position: 'relative', flex: '1 1 240px' }}>
            <Search
              size={14}
              style={{
                position: 'absolute', left: 10, top: '50%',
                transform: 'translateY(-50%)', color: C.neutralMuted, pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar skill por nome ou descrição..."
              aria-label="Buscar skills"
              style={{
                width: '100%', paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                backgroundColor: C.blackElev2, border: `1px solid ${C.blackBorder}`,
                borderRadius: 6, fontSize: 13, color: C.neutral, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ position: 'relative' }}>
            <Filter
              size={13}
              style={{
                position: 'absolute', left: 9, top: '50%',
                transform: 'translateY(-50%)', color: C.neutralMuted, pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              aria-label="Filtrar por origem"
              style={{
                paddingLeft: 28, paddingRight: 32, paddingTop: 8, paddingBottom: 8,
                backgroundColor: C.blackElev2, border: `1px solid ${C.blackBorder}`,
                borderRadius: 6, fontSize: 13, color: sourceFilter ? C.neutral : C.neutralMuted,
                outline: 'none', cursor: 'pointer', appearance: 'none',
              }}
            >
              <option value="">Todas as origens</option>
              <option value="anthropic">Anthropic (17)</option>
              <option value="tech-leads-club">Tech Leads Club (28)</option>
              <option value="marketing">Marketing (33)</option>
            </select>
          </div>

          {/* Filtro "Sem conteúdo" (A2 FR-22c) */}
          <button
            type="button"
            onClick={() => setSemConteudo((v) => !v)}
            aria-pressed={semConteudo}
            title="Mostrar só as skills sem conteúdo (só nome e descrição)"
            style={{
              padding: '8px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
              backgroundColor: semConteudo ? `${C.warn}22` : C.blackElev2,
              border: `1px solid ${semConteudo ? C.warn : C.blackBorder}`,
              color: semConteudo ? C.warn : C.neutralMuted, outline: 'none',
            }}
          >
            Sem conteúdo
          </button>

          {hasFilters && (
            <Btn variant="ghost" size="sm" icon={X} onClick={clearFilters} aria-label="Limpar filtros">
              Limpar
            </Btn>
          )}

          <div style={{ fontSize: 12, color: C.neutralMuted, marginLeft: 'auto' }}>
            {isFetching ? (
              <span style={{ color: C.cyber }}>Buscando...</span>
            ) : (
              <span>{skills.length} de {total.toLocaleString('pt-BR')} skills</span>
            )}
          </div>
        </div>
      </Card>

      {/* Tabela de skills */}
      <Card style={{ padding: 0, marginBottom: 20, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: 32 }}>
            <LoadingSpinner label="Carregando skills..." />
          </div>
        ) : skills.length === 0 ? (
          <div
            style={{ padding: 32, textAlign: 'center', color: C.neutralDim, fontSize: 14 }}
            aria-live="polite"
          >
            <Puzzle size={32} style={{ color: C.neutralMuted, marginBottom: 12 }} aria-hidden="true" />
            <div>Nenhuma skill encontrada com os filtros aplicados.</div>
            {hasFilters && (
              <Btn variant="ghost" size="sm" onClick={clearFilters} style={{ marginTop: 12 }}>
                Limpar filtros
              </Btn>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Lista de skills do catálogo">
              <thead>
                <tr style={{ backgroundColor: C.blackElev2 }}>
                  <th style={TH_STYLE} scope="col">Skill</th>
                  <th style={TH_STYLE} scope="col">Origem</th>
                  <th style={TH_STYLE} scope="col">Categoria</th>
                </tr>
              </thead>
              <tbody>
                {skills.map((skill, idx) => (
                  <SkillRow key={skill.id ?? idx} skill={skill} onSelect={setSelectedSkill} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}
          aria-label="Paginação do catálogo de skills"
        >
          <Btn
            variant="ghost" size="sm" icon={ChevronLeft}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Página anterior"
          >
            Anterior
          </Btn>
          <span style={{ fontSize: 13, color: C.neutralDim }}>
            Página <strong style={{ color: C.neutral }}>{page + 1}</strong> de{' '}
            <strong style={{ color: C.neutral }}>{totalPages}</strong>
          </span>
          <Btn
            variant="ghost" size="sm" icon={ChevronRight}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            aria-label="Próxima página"
          >
            Próxima
          </Btn>
        </div>
      )}

      {selectedSkill && (
        <SkillDetailModal skill={selectedSkill} onClose={() => setSelectedSkill(null)} />
      )}

      {showCreateSkill && <CreateSkillModal onClose={() => setShowCreateSkill(false)} />}
      {showImportSkill && <ImportSkillModal onClose={() => setShowImportSkill(false)} />}
      {showImportFile && <ImportSkillFileModal onClose={() => setShowImportFile(false)} />}
    </div>
  );
}

// ─── CatalogScreen ────────────────────────────────────────────────────────────

export function CatalogScreen() {
  const [tab,             setTab]             = useState(() => (parametroDaUrl('tab') === 'skills' ? 'skills' : 'agents'));
  const [semPrompt,       setSemPrompt]       = useState(false);
  // A busca global do topo abre o catálogo com ?search=<agente> (aba de agentes).
  const buscaDaUrl = parametroDaUrl('tab') === 'skills' ? '' : parametroDaUrl('search');
  const [search,          setSearch]          = useState(buscaDaUrl);
  const [categoryFilter,  setCategoryFilter]  = useState('');
  const [permFilter,      setPermFilter]      = useState('');
  const [onlyInUse,       setOnlyInUse]       = useState(false);
  const [page,            setPage]            = useState(0);
  const [selectedAgent,   setSelectedAgent]   = useState(null);
  const [showCreateAgent, setShowCreateAgent] = useState(false);

  // Debounce para o campo de busca
  const [debouncedSearch, setDebouncedSearch] = useState(buscaDaUrl);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reseta para página 0 quando filtros mudam
  useEffect(() => { setPage(0); }, [debouncedSearch, categoryFilter, permFilter, onlyInUse, semPrompt]);

  const filters = useMemo(() => ({
    search:           debouncedSearch || undefined,
    category:         categoryFilter  || undefined,
    permission_level: permFilter      || undefined,
    in_use:           onlyInUse || undefined,
    prompt_status:    semPrompt ? 'sem_prompt' : undefined,
    limit:            PAGE_SIZE,
    offset:           page * PAGE_SIZE,
  }), [debouncedSearch, categoryFilter, permFilter, onlyInUse, semPrompt, page]);

  const { data, isLoading, isFetching, error } = useAgents(filters);
  // KPI: quantos agentes do catálogo ainda não têm prompt de especialista (A2 FR-22a).
  const semPromptQ = useAgents({ prompt_status: 'sem_prompt', limit: 1 });
  // Totais do catálogo inteiro (sem filtro) — o cabeçalho dizia "394 · 78" fixo e ficou
  // desatualizado quando a 0099 trouxe o especialista de integração (2026-10-03).
  const totalAgentsQ = useAgents({ limit: 1 });
  const totalSkillsQ = useSkills({ limit: 1 });
  const totalDe = (d) => {
    if (!d) return null;
    if (Array.isArray(d)) return d.length;
    return d.total ?? d.count ?? null;
  };
  const totalAgentes = totalDe(totalAgentsQ.data);
  const totalSkills = totalDe(totalSkillsQ.data);
  const fmtTotal = (n) => (n == null ? '—' : n.toLocaleString('pt-BR'));
  const totalSemPrompt = useMemo(() => {
    const d = semPromptQ.data;
    if (!d) return null;
    if (Array.isArray(d)) return d.length;
    return d.total ?? d.count ?? (Array.isArray(d.items) ? d.items.length : null);
  }, [semPromptQ.data]);

  // Estrutura esperada da API: { items: Agent[], total: number } ou Agent[]
  const agents = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.items ?? []);
  }, [data]);

  const total = useMemo(() => {
    if (!data) return 0;
    if (Array.isArray(data)) return data.length;
    return data.total ?? data.count ?? agents.length;
  }, [data, agents]);

  // KPIs calculados a partir dos dados disponíveis
  const kpiTiers = useMemo(() => {
    const counts = { opus: 0, sonnet: 0, haiku: 0 };
    for (const a of agents) {
      const m = a.model?.toLowerCase();
      if (m in counts) counts[m]++;
    }
    return counts;
  }, [agents]);

  const uniqueCategories = useMemo(() => {
    const cats = new Set(agents.map((a) => a.category).filter(Boolean));
    return cats.size;
  }, [agents]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = useCallback((e) => setSearch(e.target.value), []);
  const clearFilters = useCallback(() => {
    setSearch('');
    setCategoryFilter('');
    setPermFilter('');
    setOnlyInUse(false);
    setSemPrompt(false);
  }, []);

  const hasFilters = Boolean(search || categoryFilter || permFilter || onlyInUse || semPrompt);

  // ── Error states ──────────────────────────────────────────────────────
  if (error?.status === 403) return <ForbiddenBanner />;
  if (error?.status === 429) return <RateLimitBanner />;

  return (
    <div style={{ padding: 28 }}>
      {/* Subtítulo */}
      <p style={{ fontSize: 13, color: C.neutralDim, marginBottom: 20 }}>
        {fmtTotal(totalAgentes)} sub-agents auditados (least privilege) · {fmtTotal(totalSkills)} skills
        {MOSTRAR_ENGINEERING_STANDARDS ? ' · 11 Engineering Standards obrigatórios' : ''}
      </p>

      {/* Abas: Agents | Skills */}
      <div
        role="tablist"
        aria-label="Alternar entre agents e skills"
        style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.blackBorder}` }}
      >
        <TabButton
          active={tab === 'agents'}
          onClick={() => setTab('agents')}
          icon={Bot}
          label="Agents"
          count={fmtTotal(totalAgentes)}
        />
        <TabButton
          active={tab === 'skills'}
          onClick={() => setTab('skills')}
          icon={Puzzle}
          label="Skills"
          count={fmtTotal(totalSkills)}
        />
      </div>

      {tab === 'skills' && <SkillsPanel />}

      {tab === 'agents' && (
      <>
      {/* Ações do catálogo de agents */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn variant="outline" size="sm" icon={Plus} onClick={() => setShowCreateAgent(true)}>
          Novo agente
        </Btn>
      </div>

      {/* KpiCards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <KpiCard
          label="Total de Agents"
          value={total > 0 ? total.toLocaleString('pt-BR') : fmtTotal(totalAgentes)}
          icon={Bot}
          color={C.cyber}
          sub="Catálogo auditado (least privilege)"
        />
        <KpiCard
          label="Por Tier"
          value={`${kpiTiers.opus}·${kpiTiers.sonnet}·${kpiTiers.haiku}`}
          icon={Cpu}
          color={C.quantum}
          sub="opus · sonnet · haiku"
        />
        <KpiCard
          label="Categorias"
          value={uniqueCategories > 0 ? uniqueCategories : '34'}
          icon={Layers}
          color={C.neon}
          sub="Domínios de especialização"
        />
        {MOSTRAR_ENGINEERING_STANDARDS && (
          <KpiCard
            label="Eng. Standards"
            value="11"
            icon={Shield}
            color={C.digital}
            sub="OOP+SOLID, DDD, TDD, OWASP..."
          />
        )}
        <KpiCard
          label="Sem prompt de especialista"
          value={totalSemPrompt != null ? totalSemPrompt.toLocaleString('pt-BR') : '—'}
          icon={BookOpen}
          color={C.warn}
          sub="Agentes rodando só com a descrição"
        />
      </div>

      {/* Busca e filtros */}
      <Card style={{ marginBottom: 20, padding: '14px 20px' }}>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Campo de busca */}
          <div style={{ position: 'relative', flex: '1 1 240px' }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: C.neutralMuted,
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={handleSearch}
              placeholder="Buscar por nome ou descrição..."
              aria-label="Buscar agentes"
              style={{
                width: '100%',
                paddingLeft: 30,
                paddingRight: 12,
                paddingTop: 8,
                paddingBottom: 8,
                backgroundColor: C.blackElev2,
                border: `1px solid ${C.blackBorder}`,
                borderRadius: 6,
                fontSize: 13,
                color: C.neutral,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Filtro categoria */}
          <div style={{ position: 'relative' }}>
            <Filter
              size={13}
              style={{
                position: 'absolute',
                left: 9,
                top: '50%',
                transform: 'translateY(-50%)',
                color: C.neutralMuted,
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filtrar por categoria"
              style={{
                paddingLeft: 28,
                paddingRight: 32,
                paddingTop: 8,
                paddingBottom: 8,
                backgroundColor: C.blackElev2,
                border: `1px solid ${C.blackBorder}`,
                borderRadius: 6,
                fontSize: 13,
                color: categoryFilter ? C.neutral : C.neutralMuted,
                outline: 'none',
                cursor: 'pointer',
                appearance: 'none',
              }}
            >
              <option value="">Todas as categorias</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Filtro permissão */}
          <select
            value={permFilter}
            onChange={(e) => setPermFilter(e.target.value)}
            aria-label="Filtrar por nível de permissão"
            style={{
              padding: '8px 32px 8px 12px',
              backgroundColor: C.blackElev2,
              border: `1px solid ${C.blackBorder}`,
              borderRadius: 6,
              fontSize: 13,
              color: permFilter ? C.neutral : C.neutralMuted,
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
            }}
          >
            <option value="">Todas as permissões</option>
            <option value="Read-only">Read-only</option>
            <option value="Developer">Developer</option>
            <option value="Orchestrator">Orchestrator</option>
          </select>

          {/* Toggle "Em uso" — só agentes cabeados no pipeline */}
          <button
            type="button"
            onClick={() => setOnlyInUse((v) => !v)}
            aria-pressed={onlyInUse}
            title="Mostrar só os agentes realmente cabeados no pipeline de execução"
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              backgroundColor: onlyInUse ? `${C.cyber}22` : C.blackElev2,
              border: `1px solid ${onlyInUse ? C.cyber : C.blackBorder}`,
              color: onlyInUse ? C.cyber : C.neutralMuted,
              outline: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Em uso
          </button>

          {/* Filtro "Sem prompt de especialista" (A2 FR-22a) */}
          <button
            type="button"
            onClick={() => setSemPrompt((v) => !v)}
            aria-pressed={semPrompt}
            title="Mostrar só os agentes sem prompt de especialista"
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              backgroundColor: semPrompt ? `${C.warn}22` : C.blackElev2,
              border: `1px solid ${semPrompt ? C.warn : C.blackBorder}`,
              color: semPrompt ? C.warn : C.neutralMuted,
              outline: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Sem prompt de especialista
          </button>

          {/* Limpar filtros */}
          {hasFilters && (
            <Btn
              variant="ghost"
              size="sm"
              icon={X}
              onClick={clearFilters}
              aria-label="Limpar todos os filtros"
            >
              Limpar
            </Btn>
          )}

          {/* Contador */}
          <div style={{ fontSize: 12, color: C.neutralMuted, marginLeft: 'auto' }}>
            {isFetching ? (
              <span style={{ color: C.cyber }}>Buscando...</span>
            ) : (
              <span>
                {agents.length} de {total.toLocaleString('pt-BR')} agents
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Tabela de agents */}
      <Card style={{ padding: 0, marginBottom: 20, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: 32 }}>
            <LoadingSpinner label="Carregando catálogo..." />
          </div>
        ) : agents.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: C.neutralDim,
              fontSize: 14,
            }}
            aria-live="polite"
          >
            <BookOpen size={32} style={{ color: C.neutralMuted, marginBottom: 12 }} aria-hidden="true" />
            <div>Nenhum agente encontrado com os filtros aplicados.</div>
            {hasFilters && (
              <Btn
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                style={{ marginTop: 12 }}
              >
                Limpar filtros
              </Btn>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse' }}
              aria-label="Lista de agents do catálogo"
            >
              <thead>
                <tr style={{ backgroundColor: C.blackElev2 }}>
                  <th style={TH_STYLE} scope="col">Agente</th>
                  <th style={TH_STYLE} scope="col">Modelo</th>
                  <th style={TH_STYLE} scope="col">Categoria</th>
                  <th style={TH_STYLE} scope="col">Permissão</th>
                  <th style={TH_STYLE} scope="col">Skills</th>
                  <th style={TH_STYLE} scope="col">Prompt</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent, idx) => (
                  <AgentRow
                    key={agent.id ?? idx}
                    agent={agent}
                    onSelect={setSelectedAgent}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}
          aria-label="Paginação do catálogo"
        >
          <Btn
            variant="ghost"
            size="sm"
            icon={ChevronLeft}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Página anterior"
          >
            Anterior
          </Btn>

          <span style={{ fontSize: 13, color: C.neutralDim }}>
            Página <strong style={{ color: C.neutral }}>{page + 1}</strong> de{' '}
            <strong style={{ color: C.neutral }}>{totalPages}</strong>
          </span>

          <Btn
            variant="ghost"
            size="sm"
            icon={ChevronRight}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            aria-label="Próxima página"
          >
            Próxima
          </Btn>
        </div>
      )}
      </>
      )}

      {/* Modal de detalhe */}
      {selectedAgent && (
        <AgentDetailModal
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
        />
      )}

      {/* Modal de criação de agente */}
      {showCreateAgent && (
        <CreateAgentModal onClose={() => setShowCreateAgent(false)} />
      )}
    </div>
  );
}
